import {ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output} from "@angular/core";
import * as _ from "lodash";
import {RoutesViewSettingsRouteModel} from "../../../models/routes-view-settings.model";
import {TaskModel} from "../../../../tasks/models/task.model";
import {RouteModel} from "../../../models/route.model";
import {Service} from "../../../../deliveries/models/service.model";
import {
  BringozMenuPanelComponent
} from "../../../../commons/angular/components/bringoz-menu-panel/bringoz-menu-panel.component";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {Subscription} from "rxjs";
import {AccountReferenceModel} from "../../../../account/models/account-reference.model";
import {TaskRelationModel} from "../../../../tasks/models/task-relation.model";

require('./routes-panel-route-view-task-item.scss');
@Component({
  standalone: false,
  selector: 'app-routes-panel-route-view-task-item',
  templateUrl: './routes-panel-route-view-task-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutesPanelRouteViewTaskItemComponent implements OnInit {

  @Input() task: TaskModel;
  @Input() index: number;
  @Input() isSelected: boolean;
  @Input() route?: RouteModel;
  @Input() settings: RoutesViewSettingsRouteModel;
  @Input() highlightedTask?: any;
  @Input() isHighlighted: boolean = false;
  @Input() relations: TaskRelationModel[];
  @Output() onViewJob = new EventEmitter<boolean>();
  @Output() onToggle = new EventEmitter<boolean>();
  @Output() onSplitByPackages = new EventEmitter<{ item: Service | TaskModel; event: MouseEvent }>();
  @Output() onHoverTask = new EventEmitter<{ task: TaskModel; isHover: boolean }>();
  @Output() onRelationsClick = new EventEmitter<MouseEvent>();

  public configZoneId: string;
  public purposeToServiceType: any;
  public packageItems: any[];
  public barcodesList: string[];
  public labels: string[];
  public routeColor: string;
  public dateRange: {notBefore: string, notAfter: string};
  public isHover: boolean;
  public stopRef?: any;
  public bundleServiceIds?: string[];
  public bundleWeight?: number;
  public proNumbers: string[] = [];
  public programs: { [key: string]: string };
  public accountReferences: (AccountReferenceModel & { pallets?: number })[];

  private _contextMenuSubscription: Subscription;
  private _isSplitAllowed: boolean = false;

  constructor(
    @Inject('$timeout') private $timeout,
    @Inject('splitService') private splitService,
    @Inject('servicesService') private servicesService,
    @Inject(PanelService) private panelService: PanelService
  ) {
    this.configZoneId = this.splitService.getTimeZone();
    this.purposeToServiceType = {
      Pickup: 'Delivery',
      Dropoff: 'Delivery',
      Operation: 'Operation'
    };
  }

  ngOnInit() {
    this._isSplitAllowed = this.task.taskType !== 'BundledStopover' && this.servicesService.isSplitAllowedForStatus(this.task.status);
    this.packageItems = [];
    this.barcodesList = [];
    this.labels = [];

    if (this.task.taskType === 'BundledStopover') {
      this.bundleServiceIds = _.map(this.task.bundledTaskList, (t) => {
        return t.serviceExternalId || t.serviceId;
      });

      this.bundleWeight = this.task.bundledTaskList?.reduce((sum, task: TaskModel) => {
        return (task.packageList[0] || []).items.reduce((sum, i) => {
          const itemWeight = i.weight || 0;
          return sum + itemWeight;
        }, 0);
      }, 0)
    }

    this.proNumbers = [];
    if (this.task.taskType === "BundledStopover" && this.task.bundledRouteTaskList) {
      let proNumbers = new Set<string>();
      this.task.bundledRouteTaskList.forEach(task => {
        if (task.serviceInfo?.proNumber) {
          proNumbers.add(task.serviceInfo.proNumber);
        }
      });
      this.proNumbers = Array.from(proNumbers);
    } else if (this.task.serviceInfo?.proNumber) {
      this.proNumbers.push(this.task.serviceInfo.proNumber);
    }

    this.programs = this.splitService.getSiteProgramTypesMap();
    this._setAccounts();
  }

  onHover(isHover) {
    this.isHover = isHover;
    this.onHoverTask.emit({ task: this.task, isHover });
    this.$timeout(0).then(() => {});
  }

  onRightClick(event: MouseEvent) {
    event.preventDefault();
    const panel = this.panelService.open(BringozMenuPanelComponent, event, [
      {
        name: 'Split Packages',
        action: 'splitByPackages',
        iconName: 'BOX',
        disabled: !this._isSplitAllowed,
      }], {
      position: {
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
      },
      width: 130
    });

    if (panel) {
      if (this._contextMenuSubscription) {
        this._contextMenuSubscription.unsubscribe();
      }

      this._contextMenuSubscription = panel.instance.onClose
        .subscribe((action?: string) => {
          switch (action) {
            case 'splitByPackages':
              this.onSplitByPackages.emit({ item: this.task, event: event });
              break;
            default:
              break;
          }

          this._contextMenuSubscription?.unsubscribe();
        });
    }
  }

  private _getPalletsFromPackageList(packageList: any[]): number {
    let totalPallets = 0;
    if (Array.isArray(packageList) && packageList.length) {
      packageList.forEach(pkg => {
        if (pkg?.items?.length) {
          totalPallets += pkg.items.reduce((acc, item) => {
            if (item.pallets) {
              acc += (item.pallets || 0);
            }
            return acc;
          }, 0);
        }
      });
    }
    return Number.isInteger(totalPallets) ? totalPallets : Number(totalPallets.toFixed(2));
  }

  private _setAccounts() {
    const taskList = this.task.taskType === "BundledStopover"
      ? (this.task.bundledTaskList || this.task.bundledRouteTaskList || [])
      : [];

    const accountReferences = taskList.length
        ? _.chain(taskList)
          .flatMap('accountReference')
          .compact()
          .uniqBy('id')
          .value()
        : [];

    this.accountReferences = accountReferences.length
      ? accountReferences
      : this.task.accountReference
        ? [this.task.accountReference]
        : [];

    if (this.task.taskType === "BundledStopover") {
      this.accountReferences = this.accountReferences.map(accRef => {
        const clonedAccRef = { ...accRef };
        let palletsSum = 0;
        taskList.forEach(t => {
          if (t.accountReference?.id === clonedAccRef.id) {
            palletsSum += this._getPalletsFromPackageList(t.packageList);
          }
        });
        clonedAccRef.pallets = Number.isInteger(palletsSum) ? palletsSum : Number(palletsSum.toFixed(2));
        return clonedAccRef;
      });

      if (this.accountReferences.length === 1 && !this.accountReferences[0].pallets) {
        let totalPallets = 0;
        if (Array.isArray(this.task.packageList) && this.task.packageList.length) {
          this.task.packageList.forEach(pkg => {
            if (pkg?.items?.length) {
              totalPallets += pkg.items.reduce((acc, item) => {
                if (item.pallets) {
                  acc += (item.pallets || 0);
                  // acc += (item.pallets * (item.count || 1));
                }
                return acc;
              }, 0);
            }
          });
        }
        this.accountReferences[0].pallets = Number.isInteger(totalPallets) ? totalPallets : Number(totalPallets.toFixed(2));;
      }

    } else if (this.task.taskType === "Stopover") {
      this.accountReferences = this.accountReferences.map(accRef => {
        const clonedAccRef = { ...accRef };
        const palletsSum = this._getPalletsFromPackageList(this.task.packageList);
        clonedAccRef.pallets = palletsSum;
        return clonedAccRef;
      });
    }
  }
}
